import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { MessageService } from 'primeng/api'

import { WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { BoardData, WorkItem, WorkItemStatus, WorkUserRef } from '../../data/interfaces/worklog.interface'
import { AREA_META, BOARD_VIEW_COLUMNS, DONE_COLUMN, PRIORITY_META, SEVERITY_META, STATUS_META, TODO_COLUMN, TYPE_META, WORK_ITEM_TRANSITIONS } from '../../data/constants/worklog.constants'
import { needsClassification } from '../../data/utils/classification.util'

/**
 * Vista Tablero (Kanban) del sprint activo: la columna "Por hacer" (comprometidas que aún no
 * arrancan), los estados de trabajo en el orden del flujo y, al final, «Terminadas» (ADR-0065).
 * El drag-and-drop entre columnas solo está habilitado para usuarios `isDev`; el backend valida.
 * Cada movimiento queda registrado (historial) para las métricas de TI.
 */
@Component({
  selector: 'app-worklog-board',
  templateUrl: './worklog-board.component.html',
  styleUrls: ['./worklog-board.component.scss'],
})
export class WorklogBoardComponent implements OnInit {
  @Input() isDev = false
  @Output() openItem = new EventEmitter<string>()

  board: BoardData = { sprint: null, columns: {} }
  loading = true
  dragged: WorkItem | null = null
  dropTarget: string | null = null

  readonly COLUMNS = BOARD_VIEW_COLUMNS
  readonly DONE_COLUMN = DONE_COLUMN
  readonly STATUS_META = STATUS_META
  readonly TYPE_META = TYPE_META
  readonly AREA_META = AREA_META
  readonly PRIORITY_META = PRIORITY_META
  readonly SEVERITY_META = SEVERITY_META

  //* Meta visual de "Por hacer" (no es un estado de la máquina, así que no vive en STATUS_META).
  private readonly TODO_META = { label: TODO_COLUMN, color: '#94a3b8', icon: 'fa-solid fa-inbox' }
  //* La columna es plural aunque el estado se lea en singular en la tarjeta («Terminada»).
  private readonly DONE_META = { ...STATUS_META[DONE_COLUMN], label: 'Terminadas' }

  constructor(private worklog: WorklogService, private messageService: MessageService) {}

  ngOnInit(): void {
    this.reload()
  }

  reload(): void {
    this.loading = true
    this.worklog.getBoard().subscribe((b) => {
      this.board = b || { sprint: null, columns: {} }
      this.loading = false
    })
  }

  column(col: string): WorkItem[] {
    return this.board.columns?.[col] ?? []
  }

  /** Título de la épica cuando viene poblada; el backend la manda como ref o como objeto. */
  epicTitle(it: WorkItem): string | null {
    const e: any = (it as any).epic
    return e && typeof e === 'object' ? e.title ?? null : null
  }

  colMeta(col: string): { label: string; color: string; icon: string } {
    if (col === TODO_COLUMN) return this.TODO_META
    return col === DONE_COLUMN ? this.DONE_META : (STATUS_META as any)[col]
  }

  /** Estado real de la máquina detrás de una columna de vista ("Por hacer" ⇒ 'Backlog'). */
  private statusOf(col: string): WorkItemStatus {
    return (col === TODO_COLUMN ? 'Backlog' : col) as WorkItemStatus
  }

  /** Columna de vista donde se pinta un estado ('Backlog' ⇒ "Por hacer"). */
  private colOf(status: WorkItemStatus): string {
    return status === 'Backlog' ? TODO_COLUMN : status
  }

  // ─────────── Drag & Drop (solo isDev) ───────────

  onDragStart(item: WorkItem): void {
    this.dragged = this.isDev ? item : null
  }

  onDragEnd(): void {
    this.dragged = null
    this.dropTarget = null
  }

  onDragEnter(col: string): void {
    if (this.canDropOn(col)) this.dropTarget = col
  }

  onDragLeave(): void {
    this.dropTarget = null
  }

  /** ¿El item arrastrado puede caer en esta columna? (transición válida + isDev) */
  canDropOn(col: string): boolean {
    if (!this.dragged || !this.isDev) return false
    const to = this.statusOf(col)
    if (this.dragged.status === to) return false
    return (WORK_ITEM_TRANSITIONS[this.dragged.status] ?? []).includes(to)
  }

  onDrop(col: string): void {
    const item = this.dragged
    this.dropTarget = null
    if (!item || !this.canDropOn(col)) {
      this.dragged = null
      return
    }
    const from = item.status
    const to = this.statusOf(col)
    //* To Deploy exige esfuerzo, áreas y —si tocó varias— el reparto (F-156), y el tablero no tiene
    //* dónde capturarlos: sin esto el arrastre fallaba con un error que no decía dónde arreglarlo
    //* (B-030). El formulario vive en el detalle, así que se abre ahí.
    if (needsClassification(item, to)) {
      this.dragged = null
      this.openItem.emit(item._id)
      this.messageService.add({
        key: 'worklog',
        severity: 'info',
        summary: 'Falta clasificarla',
        detail: 'Para mandarla a To Deploy indica el esfuerzo, las áreas que tocó y, si fueron varias, cuántos puntos fueron de cada una.',
        life: 5000,
      })
      return
    }
    // Movimiento optimista.
    this.moveLocally(item, from, to)
    this.dragged = null
    this.worklog.transition(item._id, to).subscribe({
      next: (updated) => {
        if (!updated) this.reload()
      },
      //* Rollback + motivo. Antes el error se tragaba en silencio y la tarjeta "regresaba
      //* sola" sin explicar por qué (403 sin isDev, transición inválida, gate de puntos, etc.).
      error: (err) => {
        this.reload()
        this.messageService.add({
          key: 'worklog',
          severity: 'error',
          summary: 'No se pudo mover',
          detail: worklogErrorMessage(err, 'No se pudo mover la tarea.'),
          life: 5000,
        })
      },
    })
  }

  private moveLocally(item: WorkItem, from: WorkItemStatus, to: WorkItemStatus): void {
    const fromCol = this.colOf(from)
    const toCol = this.colOf(to)
    const src = this.board.columns[fromCol] ?? []
    this.board.columns[fromCol] = src.filter((i) => i._id !== item._id)
    item.status = to
    this.board.columns[toCol] = [item, ...(this.board.columns[toCol] ?? [])]
  }

  // ─────────── Helpers ───────────

  /**
   * Aging WIP (ADR-0040 · Fase 2). Una tarjeta que no se mueve es el síntoma que un tablero
   * debe gritar: a los 3 días avisa, a los 7 alerta. Los umbrales son de sprint de 2 semanas —
   * más de la mitad del sprint en una sola etapa es una tarea atorada, no una tarea grande.
   */
  agingLevel(it: WorkItem): 'ok' | 'warn' | 'stale' {
    const d = it.stageDays ?? 0
    if (d >= 7) return 'stale'
    if (d >= 3) return 'warn'
    return 'ok'
  }

  agingLabel(it: WorkItem): string {
    const d = it.stageDays ?? 0
    if (d === 0) return 'Hoy'
    return `${d}d aquí`
  }

  areaMeta(a: string) {
    return (AREA_META as any)[a]
  }

  initials(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '?'
    return `${(u.names ?? '?')[0] ?? ''}${(u.lastNames ?? '')[0] ?? ''}`.toUpperCase()
  }

  userName(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '—'
    return `${u.names ?? ''} ${u.lastNames ?? ''}`.trim() || u.email || '—'
  }

  trackId(_: number, it: WorkItem): string {
    return it._id
  }
}
